import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Shield, FileText, Lock, Trash2, Download, Mail, Globe, ChevronDown, ChevronUp } from 'lucide-react';

// Privacy Policy Content
const PrivacyPolicyContent = () => {
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (id) => {
    setExpandedSections(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const sections = [
    {
      id: 'collection',
      title: 'Information We Collect',
      icon: FileText,
      content: `We collect information you provide directly:
• Account information (name, email, profile photo)
• Messages and content you share
• Voice and video call data (not recorded unless you enable recording)
• Payment information for subscriptions
• Device information and usage data

We automatically collect:
• IP address and location data
• Device type, browser, and operating system
• App usage patterns and preferences
• Cookies and similar technologies`
    },
    {
      id: 'use',
      title: 'How We Use Your Information',
      icon: Shield,
      content: `We use your information to:
• Provide and improve our services
• Enable messaging, calls, and streaming features
• Process payments and subscriptions
• Send notifications and updates
• Personalize your experience
• Ensure platform safety and security
• Comply with legal obligations
• Analyze usage to improve features`
    },
    {
      id: 'sharing',
      title: 'Information Sharing',
      icon: Globe,
      content: `We may share your information with:
• Other users (your public profile and content)
• Service providers (hosting, analytics, payments)
• Legal authorities when required by law
• Business partners with your consent

We DO NOT:
• Sell your personal data to third parties
• Share private messages without consent
• Use your data for unauthorized advertising`
    },
    {
      id: 'rights',
      title: 'Your Rights (GDPR/CCPA)',
      icon: Lock,
      content: `You have the right to:
• Access your personal data
• Correct inaccurate data
• Delete your data ("Right to be Forgotten")
• Export your data (data portability)
• Opt-out of marketing communications
• Restrict processing of your data
• Object to automated decision-making

California residents: You can opt-out of the "sale" of personal information.
EU residents: You can lodge complaints with supervisory authorities.`
    },
    {
      id: 'retention',
      title: 'Data Retention',
      icon: Trash2,
      content: `We retain your data:
• Account data: Until you delete your account
• Messages: 90 days after deletion (for abuse prevention)
• Call logs: 30 days
• Payment records: 7 years (legal requirement)
• Analytics: 26 months (anonymized)

You can request data deletion anytime from Settings > Privacy > Delete My Data.`
    },
    {
      id: 'security',
      title: 'Security Measures',
      icon: Shield,
      content: `We protect your data with:
• End-to-end encryption for messages (optional)
• TLS/SSL encryption for data transmission
• Secure data centers with 24/7 monitoring
• Regular security audits and penetration testing
• Two-factor authentication options
• Automatic session timeouts`
    },
    {
      id: 'contact',
      title: 'Contact Us',
      icon: Mail,
      content: `For privacy inquiries:
• Email: privacy@pulsateglobal.com
• Data Protection Officer: dpo@pulsateglobal.com
• Address: Pulsate Global Inc., Privacy Team

Response time: Within 30 days for GDPR/CCPA requests.`
    }
  ];

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-400 mb-4">
        Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
      </p>
      
      {sections.map((section) => (
        <div key={section.id} className="border border-white/10 rounded-xl overflow-hidden">
          <button
            onClick={() => toggleSection(section.id)}
            className="w-full flex items-center justify-between p-4 bg-[#0F0F16] hover:bg-[#151520] transition-colors text-left"
            aria-expanded={expandedSections[section.id]}
          >
            <div className="flex items-center gap-3">
              <section.icon className="w-5 h-5 text-[#00F0FF]" />
              <span className="font-medium text-white">{section.title}</span>
            </div>
            {expandedSections[section.id] ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>
          <AnimatePresence>
            {expandedSections[section.id] && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="p-4 bg-[#0A0A0F] text-sm text-gray-400 whitespace-pre-line">
                  {section.content}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
  );
};

// Terms of Service Content
const TermsOfServiceContent = () => {
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (id) => {
    setExpandedSections(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const sections = [
    {
      id: 'acceptance',
      title: '1. Acceptance of Terms',
      content: `By accessing or using Pulsate Global ("Service"), you agree to be bound by these Terms of Service. If you do not agree to these terms, do not use the Service.

You must be at least 13 years old to use Pulsate. If you are under 18, you must have parental consent.`
    },
    {
      id: 'account',
      title: '2. Account Responsibilities',
      content: `You are responsible for:
• Maintaining the confidentiality of your account
• All activities that occur under your account
• Providing accurate and complete information
• Notifying us of any unauthorized access

We reserve the right to suspend or terminate accounts that violate these terms.`
    },
    {
      id: 'conduct',
      title: '3. User Conduct',
      content: `You agree NOT to:
• Post illegal, harmful, or offensive content
• Harass, bully, or threaten other users
• Impersonate others or misrepresent your identity
• Spam, phish, or distribute malware
• Violate intellectual property rights
• Attempt to hack or disrupt the Service
• Use bots or automated systems without permission
• Share explicit content without proper age gates
• Engage in fraud or money laundering`
    },
    {
      id: 'content',
      title: '4. Content Ownership',
      content: `Your Content: You retain ownership of content you create and share. By posting, you grant Pulsate a non-exclusive, worldwide license to use, display, and distribute your content on the platform.

Our Content: Pulsate and its logos, designs, and features are our intellectual property. You may not copy, modify, or distribute our content without permission.`
    },
    {
      id: 'streaming',
      title: '5. Streaming & Monetization',
      content: `Streamers agree to:
• Follow all content guidelines and community standards
• Not stream copyrighted content without permission
• Pay applicable taxes on earnings
• Accept the 70/30 revenue split for tips

Pulsate reserves the right to remove streams or terminate accounts that violate policies.`
    },
    {
      id: 'payments',
      title: '6. Payments & Subscriptions',
      content: `Subscriptions:
• Auto-renew unless cancelled before the billing date
• No refunds for partial billing periods
• Prices may change with 30 days notice

Diamonds & Gifts:
• Are non-refundable virtual items
• Have no cash value outside the platform
• May be forfeited if account is terminated for violations`
    },
    {
      id: 'liability',
      title: '7. Limitation of Liability',
      content: `THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND.

Pulsate is not liable for:
• User-generated content
• Service interruptions or data loss
• Third-party actions or content
• Indirect, incidental, or consequential damages

Our total liability is limited to the amount you paid us in the past 12 months.`
    },
    {
      id: 'termination',
      title: '8. Termination',
      content: `You may delete your account at any time through Settings > Delete Account.

We may terminate or suspend your account for:
• Violation of these terms
• Illegal activity
• Extended inactivity (2+ years)
• At our sole discretion with notice`
    },
    {
      id: 'changes',
      title: '9. Changes to Terms',
      content: `We may update these terms periodically. We will notify you of material changes via email or in-app notification. Continued use after changes constitutes acceptance of the new terms.`
    },
    {
      id: 'governing',
      title: '10. Governing Law',
      content: `These terms are governed by the laws of the State of Delaware, USA. Disputes will be resolved through binding arbitration, except where prohibited by law.

EU residents retain their rights under EU consumer protection laws.`
    }
  ];

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-400 mb-4">
        Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
      </p>
      
      {sections.map((section) => (
        <div key={section.id} className="border border-white/10 rounded-xl overflow-hidden">
          <button
            onClick={() => toggleSection(section.id)}
            className="w-full flex items-center justify-between p-4 bg-[#0F0F16] hover:bg-[#151520] transition-colors text-left"
            aria-expanded={expandedSections[section.id]}
          >
            <span className="font-medium text-white">{section.title}</span>
            {expandedSections[section.id] ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>
          <AnimatePresence>
            {expandedSections[section.id] && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="p-4 bg-[#0A0A0F] text-sm text-gray-400 whitespace-pre-line">
                  {section.content}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
  );
};

// Legal Modal Component
export const LegalModal = ({ type, isOpen, onClose }) => {
  if (!isOpen) return null;

  const isPrivacy = type === 'privacy';

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[90] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
        role="dialog"
        aria-modal="true"
        aria-labelledby="legal-modal-title"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-lg max-h-[85vh] bg-[#0A0A0F] border border-white/10 rounded-3xl overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <div className="flex items-center gap-3">
              {isPrivacy ? (
                <Shield className="w-6 h-6 text-[#00F0FF]" />
              ) : (
                <FileText className="w-6 h-6 text-[#FFE800]" />
              )}
              <h2 id="legal-modal-title" className="text-lg font-bold text-white">
                {isPrivacy ? 'Privacy Policy' : 'Terms of Service'}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-white/5"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 scrollbar-dark">
            {isPrivacy ? <PrivacyPolicyContent /> : <TermsOfServiceContent />}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-white/10 bg-[#0F0F16]">
            <button
              onClick={onClose}
              className="w-full py-3 bg-white/10 text-white font-medium rounded-xl hover:bg-white/20 transition-colors"
            >
              Close
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Delete Data Modal
export const DeleteDataModal = ({ isOpen, onClose, onConfirm }) => {
  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (confirmText !== 'DELETE') return;
    
    setIsDeleting(true);
    try {
      await onConfirm?.();
    } finally {
      setIsDeleting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[90] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-data-title"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-sm bg-[#0A0A0F] border border-[#FF2A2A]/30 rounded-3xl p-6"
        >
          {/* Warning Icon */}
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#FF2A2A]/10 flex items-center justify-center">
            <Trash2 className="w-8 h-8 text-[#FF2A2A]" />
          </div>

          <h2 id="delete-data-title" className="text-xl font-bold text-white text-center mb-2">
            Delete All Data?
          </h2>
          <p className="text-sm text-gray-400 text-center mb-4">
            This will permanently delete your account and all associated data. This action cannot be undone.
          </p>

          {/* What will be deleted */}
          <div className="bg-[#FF2A2A]/5 border border-[#FF2A2A]/20 rounded-xl p-3 mb-4">
            <p className="text-xs text-[#FF2A2A] font-medium mb-2">This will delete:</p>
            <ul className="text-xs text-gray-400 space-y-1">
              <li>- Your profile and account</li>
              <li>- All messages and conversations</li>
              <li>- Posts, stories, and media</li>
              <li>- Stream history and earnings</li>
              <li>- Followers and following lists</li>
            </ul>
          </div>

          {/* Confirmation Input */}
          <div className="mb-4">
            <label className="block text-sm text-gray-400 mb-2">
              Type <span className="text-white font-mono">DELETE</span> to confirm
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
              placeholder="Type DELETE"
              className="w-full px-4 py-3 bg-[#0F0F16] border border-white/10 rounded-xl text-white placeholder-gray-500 outline-none focus:border-[#FF2A2A] transition-colors"
              data-testid="delete-confirm-input"
            />
          </div>

          {/* Buttons */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-3 bg-white/10 text-white font-medium rounded-xl hover:bg-white/20 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              disabled={confirmText !== 'DELETE' || isDeleting}
              className="flex-1 py-3 bg-[#FF2A2A] text-white font-medium rounded-xl hover:bg-[#FF4444] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="confirm-delete-btn"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Data Export Button
export const DataExportButton = ({ userId, token }) => {
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${API_URL}/api/users/me/export`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `pulsate-data-export-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      }
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <button
      onClick={handleExport}
      disabled={isExporting}
      className="flex items-center gap-2 px-4 py-2 bg-[#0F0F16] border border-white/10 rounded-xl text-white hover:bg-white/5 transition-colors disabled:opacity-50"
      data-testid="export-data-btn"
    >
      <Download className="w-4 h-4" />
      {isExporting ? 'Exporting...' : 'Export My Data'}
    </button>
  );
};

export default LegalModal;
